import Icon from 'components/_UI/Icon';
import LinkPage from 'components/_UI/LinkPage';
import Modal from 'components/_UI/Modal';
import Text from 'components/_UI/Text';
import DynamicProfileBackground from 'components/commons/DynamicProfileBackground';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';
import React, { useEffect, useState } from 'react'
import { Link, useNavigate,  useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { FullyCentered } from 'styled/alignment/Center';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import HoursList from '../items/HoursList';
import Contact from '../items/Contact';
import Events from '../items/Events';
import EstablishmentServicesOptions from './EstablishmentServicesOptions';
import establishmentTypes from 'theme/establishmentTypes';
import socialMedia from 'theme/socialMedia';
import { Phone, Clock, Calendar, BookOpen, MapPin, ExternalLink as ExternalLinkIcon } from 'lucide-react';
import establishmentsApi from 'api/requests/establishments';
import InfoTooltip from 'components/_UI/InfoTooltip';
import { useInvisibleButton } from 'context/general/InvisibleButton';
import ProductsForSale from 'modules/events/detail/ProductsForSale';
import paths from 'routes/paths';
import { panelAccent } from 'theme/panelAccent';

export default function CardProfile({
  data,
  nextEvents,
  nowEvents,
  onEventClick,
  isLoading,
  fullWidth = false
}) {

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [openModal, setOpenModal] = useState(false);
  const [selectedAction, setSelectAction] = useState(false);
  const [options, setOptions] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [productsOnSale, setProductsOnSale] = useState([]);
  const { invisibleButton, setInvisibleButton } = useInvisibleButton()

  function renderAction(action) {
    if (action == 'contact') {
      setOpenModal(true)
      setSelectAction('contact')
      setInvisibleButton(true)
    }
    if (action == 'hoursList') {
      setOpenModal(true)
      setSelectAction('hoursList')
      setInvisibleButton(true)
    }
    if (action == 'events') {
      setOpenModal(true)
      setSelectAction('events')
      setInvisibleButton(true)
    }
  }

  async function getOptions() {
    setLoadingOptions(true);
    try {
      const { data } = await establishmentsApi.getOptions();
      setOptions(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingOptions(false);
    }
  }

  async function getProductOnSale() {
    try {
      let establishmentSlug = searchParams.get('estabelecimento')

      if (!establishmentSlug) {
        establishmentSlug = data?.slug
      }

      const { data: response } = await establishmentsApi.getProducts(establishmentSlug)
      setProductsOnSale(response.data)
    } catch (error) {
      console.error(error)
    }
  }

  function onCloseModal() {
    setOpenModal(false)
    setInvisibleButton(false)
  }

  function onProductClick() {
    navigate(`${paths.establishments.page}/${data?.slug}`)
  }

  useEffect(() => {
    getOptions()
    getProductOnSale()
  }, [])

  const isFree = establishmentTypes?.free?.id == data?.classification?.id;

  return (
    <Container fullWidth={fullWidth}>
      <ContentThumb>
        <DynamicProfileBackground
          img={data?.backgroundImage.path}
          size={'144px'}
        />
        <CardImage status={data?.open}>
          <DynamicProfileImage
            img={data?.profileImage?.path}
            size={'66px'}
            word={data?.name}
          />
        </CardImage>
      </ContentThumb>

      <BasicInfo>
        <NameRow>
          <Text name='subtitle' paddingBottom={0}>
            {data?.name}
          </Text>
          {
            !!data?.verified && (
              <InfoTooltip
                type={'none'}
                text={'Estabelecimento verificado'}
                bgColor={colors.night}
              >
                <VerifieldIcon>
                  <Icon name='verifield' color={colors.primaryLight} />
                </VerifieldIcon>
              </InfoTooltip>
            )
          }
          <StablishmentStatus status={data?.open}>{data?.open ? 'Aberto' : 'Fechado'}</StablishmentStatus>
        </NameRow>
        <Text textAlign={'center'}>{data?.description}</Text>

        <ContentCategories>
          {
            <SpanCategories classification={isFree ? 'free' : 'paid'}>
              {isFree ? establishmentTypes.free.label : establishmentTypes.paid.label}
            </SpanCategories>
          }
          {
            data?.adulthood && <SpanCategories adulthood>+18</SpanCategories>
          }

          {data?.tagsEstablishments?.map(el => (
            <SpanCategories key={el?.name}>
              {el?.name}
            </SpanCategories>
          ))}
        </ContentCategories>

        <ContainerAddress>
          <ContentIcon>
            <MapPin size={'18px'} color={colors.primaryDashboard} />
          </ContentIcon>

          <LinkPage
            to={data?.link_map}
          >
            <Text color={colors.primaryDark} decoration='underline' >
              {data?.address?.name && `${data?.address?.name},`} {data?.address?.district && `Bairro ${data?.address?.district}`}
              {data?.address?.number && `, nº ${data?.address?.number}`}
              {data?.address?.complement && ` - ${data?.address?.complement}`}
            </Text>
          </LinkPage>
        </ContainerAddress>

        <ContentGroup items>
          {
            <Items
              rounded
              onClick={() => renderAction('hoursList')}
            >
              <ContentIcon rounded>
                <Clock size={'20px'} color={colors.primaryLight} />
              </ContentIcon>
              <Text name='small' ellipsisAt={'95%'} marginTop={'4px'} color={colors.nightDark}>
                Horários
              </Text>
            </Items>
          }

          {
            data?.menuImage?.path && (
              <Items rounded to={data?.menuImage?.path} target='_blank'>
                <ContentIcon rounded>
                  <BookOpen size={'20px'} color={colors.primaryLight} />
                </ContentIcon>
                <Text name='small' ellipsisAt={'95%'} marginTop={'4px'} color={colors.nightDark}>
                  Cardápio
                </Text>
              </Items>
            )
          }
          {
            data?.delivery_url && (
              <Items rounded to={data?.delivery_url} target='_blank'>
                <ContentIcon delivery>
                  <Icon color={colors.primaryLight} name='delivery' />
                </ContentIcon>
                <Text name='small' ellipsisAt={'95%'} marginTop={'4px'} color={colors.nightDark}>
                  Delivery
                </Text>
              </Items>
            )
          }

          {
            (data?.events?.now?.length > 0 || data?.events.next?.length > 0) &&
            <Items
              rounded
              onClick={() => renderAction('events')}
            >
              <ContentIcon rounded>
                <Calendar size={'20px'} color={colors.primaryLight} />
              </ContentIcon>
              <Text name='small' ellipsisAt={'95%'} marginTop={'4px'} color={colors.nightDark}>
                Eventos
              </Text>
            </Items>
          }

          {
            data?.phone && (
              <Items
                onClick={() => renderAction('contact')}
                rounded
              >
                <ContentIcon rounded>
                  <Phone color={colors.primaryLight} size={'20px'} />
                </ContentIcon>
                <Text name='small' ellipsisAt={'95%'} marginTop={'4px'} color={colors.nightDark}>
                  Contato
                </Text>
              </Items>
            )
          }
        </ContentGroup>

        {
          !!productsOnSale?.length &&
            <ProductsContainer>
              <ProductsForSale
                data={productsOnSale}
                onProductClick={onProductClick}
              />
            </ProductsContainer>
        }

        {
          !!data?.features?.length && (
            <SectionBlock>
              <SectionTitle>Características</SectionTitle>
              <FeaturesGrid fullWidth={fullWidth}>
                {
                  data?.features.map((el) => (
                    <FeatureChip key={el.id}>
                      <FeatureIconWrapper>
                        <Icon color={colors.primaryLight} name={el?.icon} />
                      </FeatureIconWrapper>
                      <FeatureLabel>{el?.name}</FeatureLabel>
                    </FeatureChip>
                  ))
                }
              </FeaturesGrid>
            </SectionBlock>
          )
        }

        {
          !!data?.serviceOptions?.length && options?.serviceOptions?.length &&
          <SectionBlock>
            <EstablishmentServicesOptions
              fullWidth={fullWidth}
              data={data?.serviceOptions}
              content={options?.serviceOptions}
              loading={loadingOptions}
            />
          </SectionBlock>
        }

        {
          !!data?.socialMedia?.length && (
            <SocialSection>
              <SocialSectionTitle>Redes sociais</SocialSectionTitle>
              <SocialGrid>
                {data?.socialMedia.map((el) => {
                  const matchedSocialMedia = socialMedia.find(sm => sm.name === el?.name?.toLowerCase());
                  const brandColor = matchedSocialMedia ? matchedSocialMedia.color : colors.nightDark;
                  return (
                    <SocialLink
                      href={el.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      key={el.id}
                      brandColor={brandColor}
                    >
                      <SocialLinkIcon brandColor={brandColor}>
                        <Icon name={el?.name && matchedSocialMedia?.icon} color={brandColor} />
                      </SocialLinkIcon>
                      <SocialLinkName>{el.name}</SocialLinkName>
                      <SocialArrow brandColor={brandColor}>
                        <ExternalLinkIcon size={11} />
                      </SocialArrow>
                    </SocialLink>
                  );
                })}
              </SocialGrid>
            </SocialSection>
          )
        }
      </BasicInfo>

      <Modal isOpen={openModal} onClose={onCloseModal}>
        {
          selectedAction === 'contact' &&
          <Contact data={data?.phone} />
        }

        {
          selectedAction === 'hoursList' &&
          <HoursList data={data?.dates} />
        }
        {
          selectedAction === 'events' &&
          <Events nextEvents={nextEvents || data?.events?.next} nowEvents={nowEvents || data?.events?.now} onEventClick={onEventClick} isLoading={isLoading} />
        }
      </Modal>
    </Container>
  )
}


const Container = styled(Column)`
  background: ${colors.neutralLight};
  border-radius: 12px;
  width: 100%;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);

  @media screen and (min-width: ${breakpoints.lg}) {
    width: 32.5%;
  }

  ${({ fullWidth }) => fullWidth && `
    width: 100% !important;
    height: auto;
    overflow: visible;
    border-radius: 0;
    box-shadow: none;
    border: none;
  `};
`;

const ContentThumb = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.35), transparent);
    pointer-events: none;
  }
`;

const CardImage = styled.div`
  width: 72px;
  height: 72px;
  position: absolute;
  bottom: -35px;
  border-radius: 50%;
  z-index: 2;
  background: ${colors.neutralLight};
  padding: 3px;
  box-shadow: ${({ status }) => status === true
    ? `0 0 0 3px ${colors.success}`
    : `0 0 0 3px ${colors.nightLight}`};

  & > picture > img {
    object-fit: cover;
  }
`;

const StablishmentStatus = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  font-size: ${fonts.sizes.mini};
  color: ${colors.neutral};
  border-radius: 12px;
  flex-shrink: 0;
  ${({ status }) => status === false && `
    background: ${colors.nightLight};
  `}
  ${({ status }) => status === true && `
    background: ${colors.success};
  `}
`;

const BasicInfo = styled(Column)`
  width: 100%;
  justify-content: flex-start;
  align-items: center;
  padding: 44px 16px 24px 16px;

  @media screen and (min-width: ${breakpoints.md}) {
    padding: 44px 20px 24px 20px;
  }
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex-wrap: wrap;
  padding-bottom: 8px;
  width: 100%;
`;

const VerifieldIcon = styled.div`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SpanCategories = styled.span`
  background: rgba(63, 107, 198, 0.08);
  border-radius: 16px;
  padding: 4px 12px;
  color: ${colors.primary};
  text-transform: capitalize;
  font-size: ${fonts.sizes.small};

  ${({ adulthood }) => adulthood && `
    background: ${colors.nightDark};
    color: ${colors.neutral};
  `}

  ${({ classification }) => classification === 'free' && `
    background: rgba(39, 183, 93, 0.1);
    color: ${colors.successDark};
  `}

  ${({ classification }) => classification === 'paid' && `
    background: rgba(105, 12, 228, 0.1);
    color: ${panelAccent(colors.occasionalPurple)};
  `}
`;

const ContentCategories = styled(Row)`
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 0;
`;

const ContentGroup = styled(Row)`
  border-radius: 10px;
  align-items: center;
  justify-content: flex-start;

  ${({ socialMedia }) => socialMedia && `
    flex-wrap: wrap;
    gap: 8px;
  `}

  ${({ features }) => features && `
    flex-wrap: wrap;
    gap: 8px;
  `}

  ${({ items }) => items && `
    overflow-x: auto;
    justify-content: flex-start;
    padding: 16px 0;
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  `}
`;

const ContentIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${({ rounded }) => rounded && `
    width: 40px;
    height: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    background: rgba(79, 134, 247, 0.1);
    padding: 4px;
    border-radius: 12px;
    justify-content: center;
  `};

  ${({ delivery }) => delivery && `
    width: 40px;
    height: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    background: rgba(79, 134, 247, 0.1);
    padding: 4px;
    border-radius: 12px;
    justify-content: center;
    & > svg {
      width: 24px;
      height: 24px;
    }
  `}
`;

const SocialSection = styled.div`
  width: calc(100% + 32px);
  margin-left: -16px;
  margin-right: -16px;
  padding: 16px 16px;
  background: rgba(0, 0, 0, 0.015);

  @media screen and (min-width: ${breakpoints.md}) {
    width: calc(100% + 40px);
    margin-left: -20px;
    margin-right: -20px;
    padding: 16px 20px;
  }
`;

const SocialSectionTitle = styled.p`
  font-size: ${fonts.sizes.small};
  font-weight: ${fonts.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${colors.night};
  margin-bottom: 10px;
`;

const SocialGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${colors.neutralLight};
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid rgba(0, 0, 0, 0.04);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.06);
  }
`;

const SocialLinkIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ brandColor }) => brandColor ? `${brandColor}12` : 'rgba(0,0,0,0.04)'};

  & > svg {
    width: 18px;
    height: 18px;
  }
`;

const SocialLinkName = styled.span`
  font-size: ${fonts.sizes.default};
  font-weight: ${fonts.weight.bold};
  color: ${colors.nightDark};
  text-transform: capitalize;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SocialArrow = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ brandColor }) => brandColor || colors.nightLight};
  opacity: 0.5;
  transition: opacity 0.15s ease;

  ${SocialLink}:hover & {
    opacity: 1;
  }
`;

const Items = styled(Link)`
  flex-direction: column;
  display: flex;
  justify-content: center;
  padding: 10px;
  align-items: center;
  width: 100%;
  text-decoration: none;
  border-radius: 8px;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.03);
  }

  @media screen and (min-width: 375px) {
    display: flex;
    width: 33%;
  }

  ${({ rounded }) => rounded && `
    min-width: 80px;
    max-width: 120px;
    flex-shrink: 0;
  `};

  ${({ socialMedia }) => socialMedia && `
    @media screen and (max-width: 375px) {
      width: 100px;
    }
  `}

  ${({ features }) => features && `
    cursor: default;

    &:hover {
      background: transparent;
    }

    @media screen and (max-width: 375px) {
      width: 100px;
    }
    & > p {
      text-transform: capitalize;
    }
  `}

  ${({ features, socialMedia, fullWidth }) => ((features && fullWidth) || (socialMedia && fullWidth)) && `
    @media screen and (min-width: ${breakpoints.md}) {
      width: 19%;
    }
  `};
`;

const ContainerAddress = styled(Row)`
  align-items: flex-start;
  justify-content: center;
  padding: 8px 0 16px 0;
  gap: 8px;

  & > a > p {
    padding-bottom: 0;
  }
`;

const ProductsContainer = styled.div`
  width: calc(100% + 32px);
  margin-left: -16px;
  margin-right: -16px;
  padding: 16px 16px 8px;
  background: rgba(0, 0, 0, 0.015);
  max-height: 320px;
  overflow-y: auto;

  @media screen and (min-width: ${breakpoints.md}) {
    width: calc(100% + 40px);
    margin-left: -20px;
    margin-right: -20px;
    padding: 16px 20px 8px;
  }
`;

const SectionBlock = styled.div`
  width: calc(100% + 32px);
  margin-left: -16px;
  margin-right: -16px;
  padding: 16px 16px;
  background: rgba(0, 0, 0, 0.015);

  @media screen and (min-width: ${breakpoints.md}) {
    width: calc(100% + 40px);
    margin-left: -20px;
    margin-right: -20px;
    padding: 16px 20px;
  }
`;

const SectionTitle = styled.p`
  font-size: ${fonts.sizes.small};
  font-weight: ${fonts.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${colors.night};
  text-align: center;
  margin-bottom: 8px;
  padding-top: 16px;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  width: 100%;

  ${({ fullWidth }) => fullWidth && `
    @media screen and (min-width: ${breakpoints.md}) {
      grid-template-columns: 1fr 1fr 1fr;
    }
  `};
`;

const FeatureChip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(79, 134, 247, 0.06);
`;

const FeatureIconWrapper = styled.div`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FeatureLabel = styled.span`
  font-size: ${fonts.sizes.small};
  color: ${colors.nightDark};
  line-height: 1.3;
  text-transform: capitalize;
`;
